import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Copy } from '@saleshandy/icons';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
} from '../../../../../shared/design-system/components/overlay';
import { accessibleOnClick } from '../../../../../shared/utils';
import TruncatedTextWithAutoTooltip from '../../../../../shared/components/truncated-text-with-auto-tooltip';
import Icon from '../../../../../shared/design-system/components/atoms/icon';
import { getEmailVerificationIcon } from '../../../../../shared/functions/get-email-verification-icon';
import CellFeedbackButtons from '../../table/cell-renderers/cell-feedback-buttons';
import { LeadRevealFeedbackSentiment } from '../../../helpers/leads.api';

type EmailItem = { email: string; shVerificationStatus?: string };

interface EmailListWithCopyProps {
  emails: EmailItem[];
  className?: string;
  placement?: Placement;
  showVerification?: boolean;
  feedbackProps?: {
    leadId: number;
    initialSentiment?: 'positive' | 'negative';
    initialMessage?: string;
    onFeedbackSubmitted?: (
      sentiment: LeadRevealFeedbackSentiment,
      message?: string,
    ) => void;
  };
}

const EmailListWithCopy: React.FC<EmailListWithCopyProps> = ({
  emails,
  className = 'v2-lead-email-cell',
  placement = Placement.Auto,
  showVerification = false,
  feedbackProps,
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);
  const [isFeedbackPopoverOpen, setIsFeedbackPopoverOpen] = useState(false);
  const [hasFeedbackMounted, setHasFeedbackMounted] = useState(false);

  useEffect(() => {
    const handleScrollCapture = (e: Event) => {
      if (!showPopover) return;
      const target = e.target as HTMLElement | null;
      // Don't close when user scrolls inside the popover itself.
      if (target?.closest?.('.bs-popover-inner, .popover')) {
        return;
      }
      // Close on any scroll (table/page/modal scroll).
      setShowPopover(false);
    };
    document.addEventListener('scroll', handleScrollCapture, true);
    return () => {
      document.removeEventListener('scroll', handleScrollCapture, true);
    };
  }, [showPopover]);

  const normalized = useMemo(() => {
    const cleaned = (emails || []).filter((e) => e?.email && e.email.trim());
    const first = cleaned[0] || null;
    return {
      emails: cleaned,
      firstEmail: first,
      remainingCount: Math.max(0, cleaned.length - 1),
    };
  }, [emails]);

  const handleCopy = useCallback(async (text: string) => {
    try {
      await navigator?.clipboard?.writeText(text || '');
      setCopiedEmail(text);
      setTimeout(() => setCopiedEmail(null), 2000);
    } catch (_e) {
      // ignore
    }
  }, []);

  if (!normalized.firstEmail) {
    return <div className={className}>-</div>;
  }

  const verification = showVerification
    ? getEmailVerificationIcon(
        normalized.firstEmail.shVerificationStatus || 'skip',
      )
    : null;

  return (
    <div
      className={className}
      onMouseEnter={
        feedbackProps && !hasFeedbackMounted
          ? () => setHasFeedbackMounted(true)
          : undefined
      }
    >
      {showVerification && verification?.identifier && (
        <OverlayTooltip
          placement={Placement.Bottom}
          text={verification?.toolTipText}
        >
          <Icon
            identifier={verification.identifier}
            className={verification.className}
          />
        </OverlayTooltip>
      )}

      <TruncatedTextWithAutoTooltip
        className="v2-lead-email-text"
        text={normalized.firstEmail.email || ''}
        placement={Placement.Bottom}
      />

      <div
        className="v2-lead-email-copy-icon"
        {...accessibleOnClick((e) => {
          e?.stopPropagation?.();
          handleCopy(normalized.firstEmail?.email || '');
        })}
        onMouseDownCapture={(e) => e.stopPropagation()}
      >
        <OverlayTooltip
          placement={Placement.Bottom}
          text={
            copiedEmail === normalized.firstEmail?.email
              ? 'Email Copied'
              : 'Copy Email'
          }
        >
          <span>
            {copiedEmail === normalized.firstEmail?.email ? (
              <Check />
            ) : (
              <Copy />
            )}
          </span>
        </OverlayTooltip>
      </div>

      {feedbackProps && hasFeedbackMounted && (
        <div
          className={`cell-feedback-btns-wrapper${
            isFeedbackPopoverOpen ? ' cell-feedback-btns-wrapper--active' : ''
          }`}
        >
          <CellFeedbackButtons
            leadId={feedbackProps.leadId}
            initialSentiment={feedbackProps.initialSentiment}
            initialMessage={feedbackProps.initialMessage}
            onFeedbackSubmitted={feedbackProps.onFeedbackSubmitted}
            onPopoverOpenChange={setIsFeedbackPopoverOpen}
          />
        </div>
      )}

      {normalized.remainingCount > 0 && (
        <OverlayPopover
          show={showPopover}
          onToggle={setShowPopover}
          placement={placement}
          className="v2-lead-emails-popover-container"
          content={
            <div className="v2-lead-emails-popover">
              {normalized.emails.map((email) => {
                const itemVerification = showVerification
                  ? getEmailVerificationIcon(
                      email?.shVerificationStatus || 'skip',
                    )
                  : null;
                return (
                  <div key={email.email} className="v2-lead-email-item">
                    {showVerification && itemVerification?.identifier && (
                      <OverlayTooltip
                        placement={Placement.Bottom}
                        text={itemVerification?.toolTipText}
                      >
                        <Icon
                          identifier={itemVerification.identifier}
                          className={itemVerification.className}
                        />
                      </OverlayTooltip>
                    )}
                    <TruncatedTextWithAutoTooltip
                      className="v2-lead-email-text"
                      text={email.email || ''}
                      placement={Placement.Auto}
                    />
                    <div
                      className="v2-lead-email-copy-icon"
                      {...accessibleOnClick((e) => {
                        e?.stopPropagation?.();
                        handleCopy(email.email || '');
                      })}
                      onMouseDownCapture={(e) => e.stopPropagation()}
                    >
                      <OverlayTooltip
                        placement={Placement.Bottom}
                        text={
                          copiedEmail === email.email
                            ? 'Email Copied'
                            : 'Copy Email'
                        }
                      >
                        <span>
                          {copiedEmail === email.email ? <Check /> : <Copy />}
                        </span>
                      </OverlayTooltip>
                    </div>
                  </div>
                );
              })}
            </div>
          }
          rootClose
        >
          <span
            className={`v2-lead-email-count ${showPopover ? 'active' : ''}`}
          >
            +{normalized.remainingCount}
          </span>
        </OverlayPopover>
      )}
    </div>
  );
};

export default EmailListWithCopy;
